import { useTranslation } from 'react-i18next'
import { FolderPlus } from '@digital-twins/design-system/icons'
import { LibraryItemRef } from '@digital-twins/shared-types'

import { Button } from '@/components/ui/button'

import { AddToCollectionMenu } from './AddToCollectionMenu'

export interface LibrarySelectionBarProps {
  /** The selected items, in selection order; the bar hides when empty. */
  items: Array<LibraryItemRef>
  onClear: () => void
  /** After a bulk add, keep only these items selected (the failed ones). */
  onKeepOnly: (failed: Array<LibraryItemRef>) => void
}

/**
 * Sticky bar over the Library home's multi-select. It builds no target list:
 * `AddToCollectionMenu` owns the targets, the POSTs and the toast, and hands
 * back the items that failed so the selection keeps only those.
 */
export function LibrarySelectionBar(props: LibrarySelectionBarProps) {
  const { items, onClear, onKeepOnly } = props
  const { t } = useTranslation('app')

  if (items.length === 0) {
    return null
  }

  const onBulkDone = (failed: Array<LibraryItemRef>) => {
    onKeepOnly(failed)
  }

  return (
    <div
      className="sticky bottom-4 z-30 mx-auto flex w-fit max-w-full items-center gap-3 rounded-full border border-border bg-card px-4 py-2 shadow-lg"
      data-testid="library-selection-bar"
    >
      <span className="text-sm font-medium text-foreground" data-testid="library-selection-count">
        {t('library.collections.selection.selected', { count: items.length })}
      </span>
      <AddToCollectionMenu
        items={items}
        testId="library-selection-add-menu"
        onBulkDone={onBulkDone}
        trigger={
          <Button
            type="button"
            variant="cta"
            size="sm"
            shape="pill"
            className="gap-1.5"
            data-testid="library-selection-add"
          >
            <FolderPlus className="h-4 w-4" aria-hidden="true" />
            {t('library.collections.addToCollection')}
          </Button>
        }
      />
      <Button
        type="button"
        variant="ghost"
        size="sm"
        shape="pill"
        data-testid="library-selection-clear"
        onClick={onClear}
      >
        {t('library.collections.clear')}
      </Button>
    </div>
  )
}
